import { useAsync } from "@/shared/hooks/useAsync";
import {
  DataTable,
  Flex,
  Tabs,
  TabsList,
  TabsTrigger,
  Text,
} from "@suns/design-system";
import { allPicksLoader } from "./loader/team-picks-loader";
import { LeagueId, SunsApiError } from "@suns/api";
import { TeamLogo } from "@/components/TeamLogo/TeamLogo";
import { teams } from "@/shared/utils/teams";
import { formatMoney, getValueColor } from "@/shared/utils/helper-functions";
import { useState } from "react";

interface Picks {
  id: number;
  teamId: number;
  year: number;
  round: number;
  projectedPickNumber: number | null;
  value: number | null;
  pickSplits: {
    pickOutcomes: PickOutcomes[];
  }[];
}

interface PickOutcomes {
  chance: number | null;
  teamId: number;
  value: number | null;
}

export function TeamsPicksListings() {
  const [round, setRound] = useState<string>("1");
  const { response: picks, loading, error } = useAsync(allPicksLoader, round);

  if (error) {
    throw new SunsApiError("Error loading team picks", { cause: error });
  }

  const columns = [
    {
      header: "Projection #",
      accessorKey: "projectedPickNumber",
      enableSorting: false,
    },
    {
      header: "Round #",
      accessorKey: "round",
      enableSorting: false,
    },
    {
      header: "Projected Team",
      accessorKey: "projectedTeam",
      cell: ({ row }: { row: { original: Picks } }) => {
        const pickOutcome = row.original.pickSplits[0]?.pickOutcomes[0];
        const teamId = teams.find(
          (team) => team.id === pickOutcome?.teamId
        )?.TEAM_ID;

        return (
          <Flex direction="right" align="center" justify="center" gap="sm">
            <Flex direction="right" align="center" gap="xs">
              <TeamLogo nbaTeamId={teamId} leagueId={LeagueId.NBA} size="3xs" />
            </Flex>
          </Flex>
        );
      },
      enableSorting: false,
    },
    {
      header: "Via",
      accessorKey: "via",
      cell: ({ row }: { row: { original: Picks } }) => {
        const pickOutcome = row.original.pickSplits[0]?.pickOutcomes[0];
        let teamVia;

        if (row.original.teamId === pickOutcome?.teamId) {
          teamVia = "Own";
        } else {
          const teamId = teams.find(
            (team) => team.id === row.original.teamId
          )?.TEAM_FULL_NAME;
          teamVia = teamId;
        }

        return (
          <Flex direction="right" align="center" justify="center" gap="sm">
            <Text>{teamVia}</Text>
          </Flex>
        );
      },
      enableSorting: false,
    },
    {
      header: "Chance",
      accessorKey: "chance",
      cell: ({ row }: { row: { original: Picks } }) => {
        const pickOutcome = row.original.pickSplits[0]?.pickOutcomes[0];

        return (
          <Text
            size="md"
            heading
            className={getValueColor(pickOutcome?.chance)}
          >
            {pickOutcome?.chance ? pickOutcome?.chance + "%" : "--"}
          </Text>
        );
      },
      enableSorting: false,
    },
    {
      header: "Dpv",
      accessorKey: "dpv",
      cell: ({ row }: { row: { original: Picks } }) => {
        const pickValue = row.original.pickSplits[0]?.pickOutcomes[0]?.value;

        return <Text>{formatMoney(pickValue)}</Text>;
      },
      enableSorting: false,
    },
  ];

  return (
    <Flex direction="down" gap="sm">
      <Tabs value={round} onValueChange={(value) => setRound(value)}>
        <TabsList>
          <TabsTrigger value="1">Round 1</TabsTrigger>
          <TabsTrigger value="2">Round 2</TabsTrigger>
        </TabsList>
      </Tabs>
      <DataTable
        columns={columns}
        data={picks?.picks || []}
        loading={loading}
      />
    </Flex>
  );
}
